import { DECOYS, FAVORITES } from '../data';

export type GameId = 'blur' | 'plot' | 'boss';

// One flat entry per favorite; a deck counts once and borrows its first cover.
export type Entry = { name: string; group: string; ratio: string; image?: string; plot?: string; trivia?: string };

export const ENTRIES: Entry[] = FAVORITES.flatMap(g => g.items.map(item => ({
  name: item.name,
  group: g.title,
  ratio: g.ratio,
  image: item.image ?? item.deck?.find(c => c.image)?.image,
  plot: item.plot,
  trivia: item.trivia,
})));

export const entriesOf = (group: string) => ENTRIES.filter(e => e.group === group);

export function shuffle<T>(arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const sample = <T,>(arr: readonly T[], n: number) => shuffle(arr).slice(0, n);

// Four choices: the answer, one other favorite and two titles that aren't favorites at all.
export function options(correct: Entry): string[] {
  const others = sample(entriesOf(correct.group).filter(e => e.name !== correct.name), 1).map(e => e.name);
  const decoys = sample(DECOYS[correct.group] ?? [], 3 - others.length);
  return shuffle([correct.name, ...others, ...decoys]);
}

export const preload = (entries: readonly Entry[]) => entries.forEach(e => { if (e.image) new Image().src = e.image; });

export const prefersReducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

export function loadBest(game: GameId): number {
  try { return Number(localStorage.getItem(`fav-best-${game}`)) || 0; } catch { return 0; }
}

// Stores the score if it beats the previous best; returns the best after saving.
export function saveBest(game: GameId, score: number): number {
  const best = Math.max(loadBest(game), score);
  try { localStorage.setItem(`fav-best-${game}`, String(best)); } catch { /* storage unavailable */ }
  return best;
}

export const ROUNDS = 8;

// The games on the cabinet, with the copy for their poster and info screen.
export const GAMES: { id: GameId; title: string; group: string; tagline: string; rules: string[]; love: string }[] = [
  {
    id: 'blur', title: 'Blurry cover', group: 'Bands', tagline: 'Name that band',
    rules: [
      `${ROUNDS} rounds. An album cover starts out blurry and sharpens over 8 seconds.`,
      'Pick the band. The sooner you guess, the more points.',
    ],
    love: 'Every cover is from a band I love.',
  },
  {
    id: 'plot', title: 'Badly explained plot', group: 'Movies', tagline: 'Now showing',
    rules: [
      `${ROUNDS} rounds. I explain the plot of a movie. Badly.`,
      'Pick the movie. Faster answers score more.',
    ],
    love: 'Every plot belongs to a movie I love.',
  },
  {
    id: 'boss', title: 'Boss fight', group: 'Games', tagline: 'VS. The Backlog',
    rules: [
      `The Backlog asks up to ${ROUNDS} questions about games. Six right answers defeat it.`,
      'Faster answers score more points.',
      'Wrong answers cost a heart and 5 points. Lose all 3 hearts and it is game over.',
    ],
    love: "The questions are about games I love and have sunk far too many hours into.",
  },
];
